<template>
    <b-modal :id="modalId" hide-footer size="xl" :title="modalTitle" v-model="modalVisible"
        @hide="resetTransfer">
        <template v-if="store.disallowTransferOut">
            <b-alert v-model="store.disallowTransferOut" variant="danger">
                <i class="fa fa-warning mr-1" />
                <strong>
                    This store disallows transferring out.
                </strong>
            </b-alert>
            <b-button block class="mt-3" @click="cancelTransfer" variant="primary">
                Close
            </b-button>
        </template>
        <template v-else>
            <!-- Accept and Transfer Info Banner -->
            <b-alert v-if="acceptAndTransferMode" show variant="info" class="mb-3">
                <i class="fas fa-info-circle mr-1"></i>
                <strong>Accept and Transfer</strong> will:
                <ol class="mb-0 mt-2">
                    <li>Auto-reverse any active claims</li>
                    <li>Complete PV1 verification</li>
                    <li>Transfer the prescription to the selected pharmacy</li>
                </ol>
            </b-alert>

            <!-- Active claims are reversed as part of the transfer; the operator confirms before it runs. -->
            <b-alert v-if="activeClaimCount > 0" show variant="warning" class="mb-3">
                <i class="fas fa-exclamation-triangle mr-1"></i>
                <strong>{{ activeClaimCount }} active claim(s)</strong> (paid, rejected, or price schedule)
                will be reversed before transfer.
            </b-alert>

            <!-- Shipped and still billed. The transfer will not reverse these, so say so before the operator
                 fills the form out rather than after the server refuses. -->
            <b-alert v-if="shippedWithClaimsCount > 0" show variant="danger" class="mb-3">
                <i class="fas fa-ban mr-1"></i>
                <strong>{{ shippedWithClaimsCount }} prescription(s) already shipped</strong> and are still
                billed. Their claims will not be reversed and those transfers will be stopped — reverse the
                billing separately first if they should still be transferred out.
            </b-alert>

            <!-- The count could not be read. Say so rather than let an unverified zero pass for "no claims" —
                 that reads exactly like a prescription with nothing to reverse. -->
            <b-alert v-if="activeClaimCountFailed" show variant="secondary" class="mb-3">
                <i class="fas fa-question-circle"></i>
                Could not check for active claims on
                {{ effectivePrescriptions.length === 1 ? 'this prescription' : 'these prescriptions' }}.
                If any are billed, the transfer will be stopped and the claim must be reversed first.
            </b-alert>

            <b-row>
                <b-col>
                    <b-form-group label="Transfer Type*">
                        <b-form-radio v-model="transferData.verbalTransfer" :value="true" inline>
                            Verbal
                        </b-form-radio>
                        <b-form-radio v-model="transferData.verbalTransfer" :value="false" inline>
                            Electronic / Fax
                        </b-form-radio>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <b-badge v-if="pharmacySupportsRxTransfer && !disableSurescriptsETransfer" variant="primary" class="pull-right">
                        <i class="fa fa-bolt"></i>
                        Supports Electronic RxTransfer
                    </b-badge>
                    <strong>
                        <SearchComponent :disabled="!!pharmacyDetail.id" v-model="pharmacyDetail"
                            search-u-r-l="/StoredPharmacy" :params="{ showOnlyActive: true }" create-new-object
                            label="Select Pharmacy*" createNewLabel="Create New Pharmacy"
                            :id="pharmacyDetail && pharmacyDetail.id" @input="onPharmacyChange"
                            @createNewObject="newStoredPharma" />
                    </strong>
                </b-col>
            </b-row>

            <b-row v-if="pharmacyDetail && pharmacyDetail.id">
                <b-col :class="{ 'font-weight-bolder': isPharmacistRequired }">
                    <SearchComponent :disabled="!pharmacyDetail || !pharmacyDetail.id" v-model="pharmacistDetail"
                        :search-u-r-l="`/StoredPharmacist/GetAllPharmacistsByStore/${pharmacyDetail.id}`"
                        :params="{ showOnlyActive: true }" create-new-object :label="pharmacistLabelText"
                        createNewLabel="Create New Pharmacist" :id="pharmacistDetail && pharmacistDetail.id"
                        @createNewObject="newStoredPharmacist" prepopulate />
                </b-col>
            </b-row>

            <b-row>
                <b-col :class="{ 'font-weight-bolder': isTransferReasonRequired }">
                    <SearchComponent v-model="transferReason" :id="transferReason && transferReason.id"
                        search-u-r-l="/RxTransfer/Reasons" :label="transferReasonLabel" prepopulate
                        @input="onTransferReasonChange" />
                </b-col>
            </b-row>

            <b-row v-if="shouldShowInsurancePlans">
                <b-col>
                    <h6>Plans to Include</h6>
                    <b-table id="patient-insurance-table" :items="combinedPlans" show-empty empty-text="No Plans Found">
                        <template #cell(patientPlan)="row">
                            <div v-html="row.item.patientPlan.toString()"></div>
                        </template>
                        <template #cell(include)="row">
                            <b-checkbox v-model="row.item.include" />
                        </template>
                    </b-table>
                </b-col>
            </b-row>

            <template v-if="transferData.verbalTransfer != null && !transferData.verbalTransfer">
                <b-row>
                    <b-col>
                        <b-form-group label="Note on Fax:">
                            <b-textarea v-model="transferData.note" />
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row v-if="shouldShowPrescriptionImageOption">
                    <b-col>
                        <b-form-group>
                            <b-form-checkbox v-model="transferData.appendPrescriptionImage">
                                Include Prescription Image
                                <HelpPopup>
                                    When enabled, the original prescription image will be included with the transfer fax
                                </HelpPopup>
                            </b-form-checkbox>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row v-if="shouldShowPatientDocuments">
                    <b-col>
                        <b-table show-empty empty-text="No documents found associated with Patient"
                            :api-url="`/patient/${singlePatientId}/get-documents/`" :items="patientDocProvider"
                            :fields="patientDocFields">
                            <template #cell(fileName)="data">
                                <strong>
                                    <a href="" @click.prevent="openDocument(data.item)">{{ data.value }}</a>
                                </strong>
                            </template>
                            <template #cell(select)="data">
                                <b-checkbox v-model="data.value" @change="updateDocuments(data.item.id, $event)">
                                </b-checkbox>
                            </template>
                        </b-table>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <b-form-group>
                            <label>
                                Additional Documents to Include (PDF)
                                <HelpPopup>
                                    Additional supporting documents that will be faxed in addition to the standard
                                    transfer document.
                                </HelpPopup>
                            </label>
                            <b-form-file id="uploadFile" v-model="additionalDocuments" accept=".pdf"
                                drop-placeholder="Drop file here..."
                                placeholder="Choose additional document or drop it here..." multiple>
                            </b-form-file>
                        </b-form-group>
                    </b-col>
                </b-row>
            </template>

            <b-row v-if="isTransferring" class="justify-content-center align-items-center flex-column">
                <b-spinner class="mr-2" label="Spinning" />
                <strong class="mt-2">{{ processingStatus }}</strong>
            </b-row>
            <b-row v-else>
                <b-col>
                    <b-button block class="mt-3" @click="cancelTransfer" variant="danger">
                        Cancel
                    </b-button>
                </b-col>
                <b-col>
                    <b-button :disabled="isTransferDisabled" block class="mt-3" variant="success"
                        @click="performTransfer">
                        {{ acceptAndTransferMode ? 'Accept & Transfer' : 'Transfer' }}
                    </b-button>
                </b-col>
            </b-row>
        </template>

        <!-- Child Modals -->
        <b-modal id="addNewPharmacy" hide-footer size="xl" no-close-on-backdrop title="Create New Pharmacy">
            <PharmacyDetailPage new-mode @close="$bvModal.hide('addNewPharmacy')" @created="onPharmacyCreate"
                :default-to-active="true" :hide-title="true" />
        </b-modal>

        <b-modal id="addNewPharmacist" hide-footer size="xl" no-close-on-backdrop title="Create New Pharmacist">
            <PharmacistDetailPage :disabled="!pharmacyDetail || !pharmacyDetail.id"
                :pharmacy-id="pharmacyDetail && +pharmacyDetail.id" new-mode @close="$bvModal.hide('addNewPharmacist')"
                @created="onPharmacistCreate" :default-to-active="true" :hide-title="true" />
        </b-modal>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Axios, { AxiosError } from 'axios';
import SearchComponent from '@/components/SearchComponent.vue';
import PharmacyDetailPage from '@/pages/Pharmacy/PharmacyDetailPage.vue';
import PharmacistDetailPage from '@/pages/Pharmacist/PharmacistDetailPage.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { PharmacyRecord } from '@/models/Pharmacy';
import { PharmacistRecord } from '@/models/Pharmacist';
import { Store } from '@/models/Store';
import { Patient } from '@/models/Patient';
import { Prescription, FaxForm } from '@/models/Prescription';
import { Plan } from '@/models/Plan';
import { PatientPlan } from '@/models/PatientPlan';
import { IncludeCombinedPlan } from '@/models/CombinedPlan';
import { PatientDocument } from '@/models/PatientDocument';
import { BvTableCtxObject } from 'bootstrap-vue';
import {Configuration} from '@/models/Configuration';
import { RxTransferReason, ExternalTransferPrefill } from '@/models/RxTransfer';

@Component({
    name: 'ExternalTransferModal',
    components: {
        SearchComponent,
        PharmacyDetailPage,
        PharmacistDetailPage,
        HelpPopup
    }
})
export default class ExternalTransferModal extends Vue {
    @Prop({ required: true }) prescriptions!: Array<Prescription>;
    @Prop({ required: true }) store!: Store;
    @Prop() patient?: Patient;
    @Prop({ default: false }) acceptAndTransferMode!: boolean;
    // The Rx Memo as the opening screen loaded it, so this modal can tell an operator edit from an
    // untouched box. Only the Accept and Transfer host supplies it — see acceptAndTransferMemoEdit.
    @Prop() originalMemo?: string | null;
    @Prop() prefill?: ExternalTransferPrefill;
    @Prop() modalIdOverride?: string;

    // Transfer data
    private transferData: FaxForm = {
        appendPrescriptionImage: true
    } as FaxForm;
    private pharmacyDetail: PharmacyRecord = new PharmacyRecord();
    private pharmacistDetail: PharmacistRecord | null = new PharmacistRecord();
    private transferReason: RxTransferReason = new RxTransferReason();
    private additionalDocuments: File[] = [];
    private selectedDocuments: number[] = [];
    private combinedPlans: IncludeCombinedPlan[] = [];
    private pharmacySupportsRxTransfer: boolean | null = null;
    private pharmacySupportsTMRx: boolean | null = null;
    private requirePharmacistConfig: boolean = false;
    private requireTransferReasonConfig: boolean = false;
    private includePrescriptionImageConfig: boolean = true;
    private disableSurescriptsETransfer: boolean = false;

    // State flags
    private isTransferring: boolean = false;
    private modalVisible: boolean = false;
    private processingStatus: string = 'Transferring...';

    // Active claims across the selected prescriptions, counted by the server when the modal opens.
    private activeClaimCount: number = 0;
    // How many of those prescriptions have already shipped. The transfer will not reverse billing for
    // product the patient is holding, so these are named separately and are not offered for reversal.
    private shippedWithClaimsCount: number = 0;
    // Set when the count could not be read at all. The transfer still goes through the server's own
    // active-claim guard, but the operator is told the warning is unverified rather than shown a zero.
    private activeClaimCountFailed: boolean = false;
    // The in-flight count request, so a fast operator cannot submit ahead of the answer.
    private activeClaimCountLoad: Promise<void> | null = null;
    private activeClaimCountRequest: number = 0;

    get modalId(): string {
        if (this.modalIdOverride) return this.modalIdOverride;
        return this.acceptAndTransferMode ? 'accept-and-transfer-modal' : 'external-transfer-modal';
    }

    get modalTitle(): string {
        return this.acceptAndTransferMode ? 'Accept and Transfer' : 'External Transfer';
    }

    get effectivePrescriptions(): Array<Prescription> {
        return this.prescriptions;
    }

    /**
     * The Rx Memo to post with an Accept and Transfer, or null to leave the stored one alone.
     *
     * PV1 transfers straight out of its own memo box without ever posting the prescription, so an edit
     * the operator typed there only reaches the database through the transfer request. Posting it
     * unconditionally would send the memo as of page load whenever the box was untouched, and the
     * server prefers any non-null value over the stored one — so a memo changed behind the screen (an
     * Asembia refresh, or a colleague on Prescription Details) would be overwritten by stale text that
     * also ends up in the TRANSFER INFO block and on the wire. Only an actual edit is sent.
     *
     * Three states reach the server: absent (the plain transfer modal, which never speaks for the
     * memo), null (untouched box, so the stored value stands), and a string (what the operator left in
     * the box, empty string included — that is a deliberate clear, not an absent value).
     */
    private get acceptAndTransferMemoEdit(): string | null {
        const current = this.effectivePrescriptions[0]?.memo ?? null;
        // No baseline supplied: post the live value rather than silently dropping the edit.
        if (this.originalMemo === undefined) return current;
        return current === (this.originalMemo ?? null) ? null : current;
    }

    get singlePatientId(): number | undefined {
        const rxList = this.effectivePrescriptions;
        if (!rxList?.length) return undefined;
        const firstPatientId = rxList[0].patientID;
        const allSamePatient = rxList.every(rx => rx.patientID === firstPatientId);
        return allSamePatient ? firstPatientId : undefined;
    }

    get shouldShowInsurancePlans(): boolean {
        return !!this.singlePatientId &&
               this.combinedPlans.length > 0 &&
               this.transferData.verbalTransfer === false;
    }

    get shouldShowPatientDocuments(): boolean {
        return !!this.singlePatientId &&
               this.transferData.verbalTransfer === false;
    }

    get shouldShowPrescriptionImageOption(): boolean {
        return this.includePrescriptionImageConfig &&
               this.transferData.verbalTransfer === false &&
               this.effectivePrescriptions.some(rx => rx.imageID != null || rx.escriptID != null);
    }

    get isPharmacistRequired(): boolean {
        // If configuration requires pharmacist, always required
        if (this.requirePharmacistConfig) {
            return true;
        }

        // For verbal transfers when config is false, pharmacist is optional
        if (this.transferData.verbalTransfer === true) {
            return false;
        }

        // For electronic/fax transfers: check if pharmacy supports RxTransfer
        if (!this.transferData.verbalTransfer && (this.pharmacySupportsRxTransfer || this.pharmacySupportsTMRx)) {
            return false;
        }

        // For electronic/fax transfers without confirmed RxTransfer/TMRx support, pharmacist is required
        if (this.transferData.verbalTransfer === false && this.pharmacySupportsRxTransfer !== true && !this.pharmacySupportsTMRx) {
            return true;
        }

        // Default to not required (should not normally reach here)
        return false;
    }

    get pharmacistLabelText(): string {
        return this.isPharmacistRequired
            ? "Select Pharmacist*"
            : "Select Pharmacist (Optional)";
    }

    get isTransferReasonRequired(): boolean {
        // External transfers are always outbound, so the toggle alone governs the requirement.
        return this.requireTransferReasonConfig;
    }

    get transferReasonLabel(): string {
        return this.isTransferReasonRequired ? "Transfer Reason*" : "Transfer Reason (Optional)";
    }

    get isTransferDisabled(): boolean {
        // Transfer type must be selected
        if (this.transferData.verbalTransfer == null) return true;

        // Pharmacy is always required for both verbal and electronic/fax
        if (!this.pharmacyDetail.id) return true;

        // If pharmacist is required but not selected, disable the button
        if (this.isPharmacistRequired && !this.pharmacistDetail?.id) {
            return true;
        }

        // If transfer reason is required but not selected, disable the button
        if (this.isTransferReasonRequired && !this.transferReason?.id) {
            return true;
        }

        // All required fields are filled
        return false;
    }

    onTransferReasonChange() {
        this.transferData.rxTransferReasonId = this.transferReason?.id ?? null;
    }

    get patientDocFields() {
        return [
            "fileName",
            "select",
        ];
    }

    @Watch('singlePatientId')
    async loadInsurancePlans() {
        if (!this.singlePatientId) {
            this.combinedPlans = [];
            return;
        }

        try {
            type PlanResponse = { plans: Plan[]; patientPlans: PatientPlan[]; };
            const response = await Axios.get<PlanResponse>(`/api/Insurance/${this.singlePatientId}`);
            this.combinedPlans = response.data.patientPlans
                .filter(pp => pp.active)
                .map(pp => new IncludeCombinedPlan({
                    patientPlan: new PatientPlan(pp),
                    plan: new Plan(response.data.plans.find(p => p.id == pp.planId)),
                    include: false,
                } as IncludeCombinedPlan));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            this.combinedPlans = [];
        }
    }

    /**
     * Identity of the current selection. Watched instead of `effectivePrescriptions` itself because hosts
     * pass a freshly built array on every render (`:prescriptions="[prescription]"`), which would re-fetch
     * the count on every unrelated re-render.
     */
    get selectionKey(): string {
        return this.effectivePrescriptions
            .map(rx => Prescription.createRxId(rx.storeID, rx.rxNumber, rx.rfNumber))
            .join(',');
    }

    /**
     * Re-counts when the selection changes under an open modal. Hosts populate the prescriptions prop
     * around the same time they show the modal — RxTransferQueuePage has a load-bearing `$nextTick` to get
     * them in first — so a count taken only on open can be a count of the previous selection, or of none.
     */
    @Watch('selectionKey')
    onSelectionChange() {
        if (!this.modalVisible) return;
        this.activeClaimCountLoad = this.loadActiveClaimCount();
    }

    @Watch('modalVisible')
    async onModalVisibilityChange(isVisible: boolean) {
        if (isVisible) {
            this.activeClaimCountLoad = this.loadActiveClaimCount();
        }
        if (isVisible && this.singlePatientId) {
            await this.loadInsurancePlans();
        }
        if (isVisible && this.acceptAndTransferMode) {
            this.prepopulateTransferPharmacy();
        }
        if (isVisible && this.prefill) {
            this.applyPrefill();
        }
    }

    /**
     * Asks the server how many active claims the selected prescriptions carry, which drives both the
     * warning banner and the reversal confirmation.
     *
     * Deliberately not derived from `prescription.claims`. The screens that open this modal get their
     * prescriptions from different endpoints and none of them hydrate claims: the print queue and bulk
     * transfer-out send `claims: null`, and a prescription reloaded after a save drops whatever the billing
     * screen had attached. Reading those payloads reported zero active claims on a billed prescription, so
     * no warning and no prompt appeared and the operator filled in the whole form only to be told the
     * prescription has an active claim. One server call also beats one claims request per prescription,
     * which does not scale to a print-queue selection.
     *
     * Advisory only — the transfer re-reads claims and is authoritative about what gets reversed. A failed
     * load leaves the count at zero, which falls back to the pre-existing behavior rather than prompting
     * for a reversal that may not be needed (confirming one costs the operator the billing permission).
     */
    private async loadActiveClaimCount(): Promise<void> {
        this.activeClaimCount = 0;
        this.shippedWithClaimsCount = 0;
        this.activeClaimCountFailed = false;

        // Built from the primitives rather than the `rxID`/`hasFullId` getters: entry points hand this modal
        // prescriptions that are not always Prescription instances, and a missing prototype getter would
        // silently drop the prescription from the count. An unsaved Rx has no id to ask about — and no
        // claims either.
        const rxIds = this.effectivePrescriptions
            .filter(rx => rx.storeID && rx.rxNumber && rx.rfNumber != null)
            .map(rx => Prescription.createRxId(rx.storeID, rx.rxNumber, rx.rfNumber));
        if (!rxIds.length) return;

        // Reopening the modal on a different selection while a count is in flight must not let the old
        // answer land on the new prescriptions.
        const request = ++this.activeClaimCountRequest;

        // Chunked so a large selection cannot fail as a unit. The print queue's "select all for patients"
        // expands to every matching row in the filtered set, not a page, so this is reachable in practice —
        // and one oversized request rejected wholesale would report zero claims for the entire batch.
        const chunkSize = 100;
        const chunks: string[][] = [];
        for (let i = 0; i < rxIds.length; i += chunkSize) chunks.push(rxIds.slice(i, i + chunkSize));

        const results = await Promise.all(chunks.map(async chunk => {
            try {
                const response = await Axios.get<Array<{ rxId: string; activeClaimCount: number; hasShipped: boolean }>>(
                    '/Prescription/active-claim-counts', { params: { rxIds: chunk.join(',') } });
                return {
                    claims: response.data.reduce((total, rx) => total + (rx.activeClaimCount ?? 0), 0),
                    // Counted per prescription, not per claim: what the operator needs is how many of the
                    // scripts in front of them cannot have their billing touched.
                    shipped: response.data.filter(rx => rx.hasShipped && (rx.activeClaimCount ?? 0) > 0).length,
                };
            } catch (err) {
                console.warn('Failed to load active claim counts for transfer:', err);
                return null;
            }
        }));

        if (request !== this.activeClaimCountRequest) return;

        // A partial answer is still worth showing — it can only under-report, and the operator gets the
        // unverified notice either way.
        this.activeClaimCountFailed = results.some(result => result === null);
        this.activeClaimCount = results.reduce((total: number, result) => total + (result?.claims ?? 0), 0);
        this.shippedWithClaimsCount = results.reduce((total: number, result) => total + (result?.shipped ?? 0), 0);
    }

    private prepopulateTransferPharmacy() {
        const transferPharmacies = this.prescriptions[0]?.program?.transferToPharmacies;
        if (!transferPharmacies?.length) return;

        this.pharmacyDetail = new PharmacyRecord(transferPharmacies[0]);
    }

    private applyPrefill() {
        if (!this.prefill?.pharmacyId) return;

        const pharmacy = new PharmacyRecord();
        pharmacy.id = this.prefill.pharmacyId;
        this.pharmacyDetail = pharmacy;
        this.onPharmacyChange(pharmacy);
    }

    @Watch('includePrescriptionImageConfig')
    onIncludePrescriptionImageConfigChange(newValue: boolean) {
        // If configuration is disabled, force appendPrescriptionImage to false
        if (!newValue) {
            this.transferData.appendPrescriptionImage = false;
        }
    }

    @Watch("pharmacyDetail")
    async handlePharmacyChange(newValue: PharmacyRecord, oldValue: PharmacyRecord) {
        try {
            if (!newValue.id || newValue.id === oldValue.id) return;
            this.pharmacySupportsRxTransfer = null;
            this.pharmacySupportsTMRx = newValue.supportsTransferMyRx;
            const resp = await Axios.get(`/RxTransfer/TransferDirectoryLookup/${this.pharmacyDetail.id}`);
            this.pharmacySupportsRxTransfer = true;
            this.pharmacyDetail = resp.data;
        } catch {
            this.pharmacySupportsRxTransfer = false;
        }
    }

    onPharmacyChange(_value: PharmacyRecord) {
        if (this.pharmacyDetail.id) {
            Axios.get<PharmacyRecord>(`/StoredPharmacy/${this.pharmacyDetail.id}`).then(
                (response) => {
                    this.pharmacyDetail = new PharmacyRecord(response.data);
                    this.transferData.name = this.pharmacyDetail.name;
                    this.transferData.address = this.pharmacyDetail.address;
                    this.transferData.city = this.pharmacyDetail.addressCity;
                    this.transferData.state = this.pharmacyDetail.addressState;
                    this.transferData.zip = this.pharmacyDetail.addressZip;
                    this.transferData.phone = this.pharmacyDetail.phoneNumber;
                    this.transferData.fax = this.pharmacyDetail.faxNumber;
                    this.transferData.dea = this.pharmacyDetail.dea;
                    this.transferData.pharmacist = this.pharmacistDetail?.lastName
                        ? `${this.pharmacistDetail.lastName}, ${this.pharmacistDetail.firstName}`
                        : this.pharmacyDetail.pharmacist;
                    this.transferData.storedPharmacyId = this.pharmacyDetail.id;
                },
            ).catch(err => this.$notification(NotificationOptions.error(err)));
        }
    }

    newStoredPharma() {
        this.pharmacyDetail = new PharmacyRecord();
        this.$bvModal.show("addNewPharmacy");
    }

    newStoredPharmacist() {
        this.pharmacistDetail = new PharmacistRecord();
        this.$bvModal.show("addNewPharmacist");
    }

    onPharmacyCreate(newPharma: PharmacyRecord) {
        this.pharmacyDetail = newPharma;
        this.$bvModal.hide("addNewPharmacy");
    }

    onPharmacistCreate(newPharmacist: PharmacistRecord) {
        this.pharmacistDetail = newPharmacist;
        this.$bvModal.hide("addNewPharmacist");
    }

    async openDocument(pd: PatientDocument) {
        try {
            const res = await Axios.get(`image/${pd.imageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(res.data);
            window.open(objectUrl, '_blank');
        } catch (err) {
            console.error("Error while getting image to show.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    updateDocuments(documentId: number, checked: boolean) {
        if (checked) {
            this.selectedDocuments.push(documentId);
        } else {
            this.selectedDocuments = this.selectedDocuments.filter((id) => id !== documentId);
        }
    }

    async patientDocProvider(ctx: BvTableCtxObject) {
        try {
            const response = await Axios.get<Array<PatientDocument>>(ctx.apiUrl!);
            return response.data.map((r) => new PatientDocument(r));
        } catch (err) {
            console.error("Error while getting patient documents", err);
            return [];
        }
    }

    /**
     * Prompts before reversing billing as part of the transfer, and records the operator's answer on the
     * request. Returns false only when the operator declines, which cancels the transfer. With no active
     * claims there is nothing to reverse, so no prompt is shown and the flag stays off.
     *
     * Billed prescriptions that already shipped are never part of what is offered here. The server will
     * not reverse them, so when every selected prescription is in that state there is nothing left to
     * confirm and the transfer is stopped where the operator can still act on it.
     */
    private async confirmClaimReversal(): Promise<boolean> {
        // The count is fetched when the modal opens; an operator who submits before it lands still gets the
        // prompt rather than walking into the server's active-claim block.
        await this.activeClaimCountLoad;

        const claimCount = this.activeClaimCount;
        if (claimCount === 0) {
            this.transferData.reverseActiveClaims = false;
            return true;
        }

        const rxCount = this.effectivePrescriptions.length;

        // Every one of them shipped: the server would refuse each in turn, so say it once, here, instead
        // of spending a round trip to be told the same thing per prescription.
        if (this.shippedWithClaimsCount >= rxCount) {
            this.$notification(NotificationOptions.error(
                rxCount === 1
                    ? 'This prescription has already shipped and is still billed. Reverse the billing ' +
                      'separately before transferring it out.'
                    : 'These prescriptions have all already shipped and are still billed. Reverse the ' +
                      'billing separately before transferring them out.'
            ));
            return false;
        }

        const subject = rxCount === 1 ? 'this prescription' : `these ${rxCount} prescriptions`;
        const shippedNote = this.shippedWithClaimsCount > 0
            ? ` ${this.shippedWithClaimsCount} of them already shipped — those will not be reversed and will not transfer.`
            : '';
        const confirmed = await this.$bvModal.msgBoxConfirm(
            `${claimCount} active claim(s) on ${subject} will be reversed as part of this transfer.` +
            `${shippedNote} Continue?`,
            {
                title: 'Reverse Claims and Transfer',
                okTitle: 'Reverse and Transfer',
                okVariant: 'warning',
                cancelTitle: 'Cancel',
                centered: true,
            });

        if (!confirmed) return false;

        this.transferData.reverseActiveClaims = true;
        return true;
    }

    async performTransfer() {
        if (this.isTransferring) return;
        try {
            // Validate that pharmacist is provided when required
            if (this.isPharmacistRequired && !this.pharmacistDetail?.id) {
                this.$notification(NotificationOptions.error('Pharmacist name is required for this transfer'));
                return;
            }

            // Validate that a transfer reason is provided when required
            if (this.isTransferReasonRequired && !this.transferReason?.id) {
                this.$notification(NotificationOptions.error('Transfer Reason is required for this transfer'));
                return;
            }

            // Reversing billing is not undoable from here, so it needs an explicit yes. Accept and Transfer
            // already states the reversal in its own banner and button, so it is not prompted twice.
            if (!this.acceptAndTransferMode && !await this.confirmClaimReversal()) return;

            this.isTransferring = true;
            if (this.acceptAndTransferMode) {
                this.processingStatus = 'Processing Accept and Transfer...';
            } else if (this.transferData.reverseActiveClaims) {
                // Reversal is a synchronous round trip to the clearinghouse (up to its configured timeout,
                // 90s by default), so say what is happening rather than sitting on a bare "Transferring...".
                this.processingStatus = 'Reversing claims, then transferring...';
            } else {
                this.processingStatus = 'Transferring...';
            }

            if (this.pharmacistDetail?.lastName) {
                this.transferData.pharmacist = `${this.pharmacistDetail.lastName}, ${this.pharmacistDetail.firstName}`;
            }

            this.transferData.patientPlanIds = this.combinedPlans
                .filter(v => v.include)
                .map(p => +p.patientPlan.id);

            (this.transferData as any).prescriptionIds = this.effectivePrescriptions.map(rx => rx.rxID);
            (this.transferData as any).documentIds = this.selectedDocuments;

            // Only Accept and Transfer speaks for the Rx Memo: the plain transfer modal is opened from
            // queues where the memo is not editable, and posting a value there would rewrite the memo of
            // every prescription in the batch. Assigned unconditionally within that mode, the null
            // included, so a retry after a validation error cannot reuse the previous attempt's value.
            // Must stay above the "record" append below, which is what actually sends it.
            if (this.acceptAndTransferMode) {
                this.transferData.rxMemo = this.acceptAndTransferMemoEdit;
            }

            const formData = new FormData();
            formData.append("record", JSON.stringify(this.transferData));

            this.additionalDocuments.forEach((file, index) => {
                formData.append(`addDocuments[${index}]`, file);
            });

            const postOptions = {
                headers: { 'Content-Type': 'multipart/form-data' },
            };

            let response;
            if (this.acceptAndTransferMode) {
                // Accept-and-transfer only works with a single prescription
                if (this.effectivePrescriptions.length !== 1) {
                    this.$notification(NotificationOptions.error('Accept and Transfer requires exactly one prescription'));
                    return;
                }
                const rx = this.effectivePrescriptions[0];
                const prescriptionId = `${rx.storeID}-${rx.rxNumber}-${rx.rfNumber}`;

                response = await Axios.post(
                    `/Prescription/${prescriptionId}/accept-and-transfer`,
                    formData,
                    postOptions
                );

                // A failed reversal is returned as an error by the server, so a 2xx here means every claim
                // reversed cleanly — there is no partial-success case left to warn about.
                const result = response.data;
                this.$notification(NotificationOptions.success(
                    `Prescription accepted and transferred via ${result.transferType || 'External'}`));
            } else {
                response = await Axios.post(`/Prescription/external-transfer`, formData, postOptions);

                const prescriptionCount = this.effectivePrescriptions.length;
                const failed: Array<{ rxId: string; error: string }> = response.data?.failed ?? [];

                if (failed.length > 0) {
                    // Part of the batch transferred and part did not. This is still a success response so
                    // the queue refreshes — reporting it as a flat failure would leave the operator thinking
                    // nothing happened while some prescriptions have already reversed and faxed.
                    const transferredCount = response.data?.transferred?.length ?? 0;
                    this.$notification(NotificationOptions.warning(
                        `${transferredCount} of ${transferredCount + failed.length} prescriptions transferred.\n` +
                        failed.map(f => `${f.rxId}: ${f.error}`).join('\n')
                    ));
                } else if (prescriptionCount === 1) {
                    const transferType = response.data?.transferType || 'External';
                    this.$notification(NotificationOptions.success(`Prescription transferred via ${transferType}`));
                } else {
                    this.$notification(NotificationOptions.success('Prescriptions transferred'));
                }
            }

            this.$emit('transferred', response.data);
            this.cancelTransfer();
        } catch (error: any) {
            this.$notification(NotificationOptions.error(error));
            this.$emit('error', error);
        } finally {
            this.isTransferring = false;
            this.processingStatus = 'Transferring...';
        }
    }

    cancelTransfer() {
        this.$bvModal.hide(this.modalId);
    }

    resetTransfer() {
        // reverseActiveClaims is not carried over: the next transfer must earn its own confirmation.
        this.transferData = {
            appendPrescriptionImage: true
        } as FaxForm;
        this.pharmacyDetail = new PharmacyRecord();
        this.pharmacistDetail = new PharmacistRecord();
        this.transferReason = new RxTransferReason();
        this.additionalDocuments = [];
        this.selectedDocuments = [];
        this.pharmacySupportsRxTransfer = null;
        this.pharmacySupportsTMRx = null;
        this.activeClaimCount = 0;
        this.shippedWithClaimsCount = 0;
        this.activeClaimCountFailed = false;
        this.activeClaimCountLoad = null;
        this.isTransferring = false;
        this.modalVisible = false;
        this.$emit('closed');
    }

    async mounted() {
        this.loadInsurancePlans();
        await this.fetchConfiguration();
    }

    async fetchConfiguration() {
        try {
            const response = await Axios.get<Record<string, boolean>>(
                '/configuration/RequirePharmacistOnExternalTransfers,IncludePrescriptionImageOnExternalTransfers,DisableSurescriptsETransfer,RequireTransferReasonOnOutboundTransfers'
            );

            this.requirePharmacistConfig = response.data.RequirePharmacistOnExternalTransfers ?? false;
            this.includePrescriptionImageConfig = response.data.IncludePrescriptionImageOnExternalTransfers ?? true;
            this.disableSurescriptsETransfer = response.data.DisableSurescriptsETransfer ?? false;
            this.requireTransferReasonConfig = response.data.RequireTransferReasonOnOutboundTransfers ?? false;

            if (!this.includePrescriptionImageConfig) {
                this.transferData.appendPrescriptionImage = false;
            }
        } catch (err) {
            console.warn('Failed to fetch configuration:', err);
            this.requirePharmacistConfig = false;
            this.includePrescriptionImageConfig = true;
            this.disableSurescriptsETransfer = false;
            this.requireTransferReasonConfig = false;
        }
    }
}
</script>

<style scoped>
/* Add any specific styles if needed */
</style>
